import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { Box, Button, Skeleton, Stack } from '@mui/material';
import { FC, KeyboardEvent, memo, MouseEvent } from 'react';
import ReactGA from 'react-ga4';
import { vehicleRowGroupKey } from 'src/components/dispatch/vehicleAssignment/vehicleSortableGroups';
import { VEHICLE_LIST_HEADER_HEIGHT } from 'src/components/dispatch/vehicleCardStyles';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { VehicleRowGroup } from './adapter';
import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleRowView } from './VehicleRowView';

type Props = {
  rowGroups: VehicleRowGroup[];
  isLoading: boolean;
  visibleColumns: number;
  displayMetrics: Set<DisplayMetric>;
  selectedCardIds: Set<string>;
  duplicateStoreNames: Set<string>;
  routeDeviationCardIds: Set<string>;
  geoAlerts: Map<string, GeoAlertInfo>;
  hasFixedRoutes: boolean;
  /** フィルタ前の delivery が 1 件以上存在するか。フィルタで全件外れたケースを
   *  「配車計画なし」と誤判定しないために使う。 */
  hasAnyDelivery: boolean;
  /** ドラッグ中の行外ドロップ位置プレビュー (青い縦バーで「ここに入る」を表示) */
  dropPreview: { rowId: string; index: number } | null;
  /** 直近のドロップで動いたカード id。一定時間パルスアニメで強調表示する。 */
  recentlyMovedCardIds: Set<string>;
  onSelectCard: (
    card: DispatchCard,
    vehicle: Vehicle,
    orderIndex: number,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
};

const CARD_WIDTH = 160;

const ConvenienceDispatchBoard: FC<Props> = memo(({
  rowGroups,
  isLoading,
  visibleColumns,
  displayMetrics,
  selectedCardIds,
  duplicateStoreNames,
  routeDeviationCardIds,
  geoAlerts,
  hasFixedRoutes,
  hasAnyDelivery,
  dropPreview,
  recentlyMovedCardIds,
  onSelectCard,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={0.5} sx={{ p: 1 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={72} />
        ))}
      </Stack>
    );
  }

  // ヘッダ列数: visibleColumns と実データ最大カード数の大きい方（route-planner準拠）
  const actualMax = Math.max(
    0,
    ...rowGroups.flatMap((g) => g.rows.map((r) => r.cards.length)),
  );
  const maxSlots = Math.max(visibleColumns, actualMax, 1);

  // 配車計画が 1 件もない場合は、列番号ヘッダや minWidth: 'max-content' を出さず、
  // スクロールコンテナの可視領域内で中央に空状態メッセージを表示する。
  // (列数によって「中央」位置が変わってしまうのを防ぐため)
  if (rowGroups.length === 0 && !hasAnyDelivery) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 320,
          py: 8,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: 56,
            width: 56,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            bgcolor: 'hsl(var(--muted))',
            mb: 1.5,
          }}
        >
          <RouteOutlinedIcon sx={{ fontSize: 28, color: 'hsl(var(--muted-foreground))' }} />
        </Box>
        <Box component="p" sx={{ fontSize: 14, fontWeight: 500, color: 'hsl(var(--muted-foreground))', mb: 0.5, m: 0 }}>
          配車計画がありません
        </Box>
        <Box component="p" sx={{ fontSize: 12, color: 'hsl(var(--muted-foreground) / 0.7)', lineHeight: 1.6, mb: 2, m: 0 }}>
          固定コースの土台を読み込んで、配車計画を作成してください。
        </Box>
        {hasFixedRoutes && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RouteOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '固定ルート 呼び出し (空状態)',
              });
              // ツールバーの「固定ルート」ボタンと同じメニューを開く（route-planner サンプル準拠）
              const trigger = document.querySelector<HTMLElement>('[data-fixed-route-trigger]');
              trigger?.click();
            }}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            固定コースの土台を呼び出す
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 'max-content' }}>
      {/* 列番号ヘッダ */}
      <Box
        data-testid="lane-number-header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          // 左の車両リストのヘッダと同じ高さにする。カード高を借りると、カードの見た目を
          // 直したときにここだけ追随して左右の行がずれる
          height: VEHICLE_LIST_HEADER_HEIGHT,
          alignItems: 'flex-end',
          pb: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          // 不透明にする。半透明だとスクロール時にヘッダの下をカードが通って透け、
          // 列番号が読めなくなる（車両リストのヘッダも同じ理由で不透明にしている）
          bgcolor: 'hsl(var(--secondary))',
          pr: 1.5,
        }}
      >
        {/* VehicleRowView の px:1.5 + 内側カード間 gap:1 と微妙にずれていたため、
            列ヘッダのカード相当ブロックを少し右にオフセットする (pl で +12px 程度) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3, pr: 1.5 }}>
          {Array.from({ length: maxSlots }, (_, index) => (
            <Box
              key={index}
              data-testid="lane-number-box"
              sx={{
                display: 'flex',
                height: 32,
                width: CARD_WIDTH,
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                border: '1px dashed hsl(var(--border))',
                bgcolor: 'hsl(var(--background))',
                fontSize: 12,
                fontWeight: 600,
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              {index + 1}
            </Box>
          ))}
        </Box>
      </Box>

      {rowGroups.map((group, groupIndex) => {
        const { vehicle, rows } = group;
        const vehicleEnabled = true; // delivery がある vehicle のみ表示されるため常に true
        const isLastGroup = groupIndex === rowGroups.length - 1;

        return (
          // 1 台に 2 つの勤務が乗ると同じ車両のグループが 2 つ並ぶので、勤務まで含めた
          // 識別子を使う（車両 id だけだと React key が重複する）
          <Box key={`${vehicleRowGroupKey(group)}-cards`}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                borderRadius: '12px',
                bgcolor: 'hsl(var(--secondary) / 0.2)',
                px: 1.5,
                py: 0.75,
              }}
            >
              {rows.map((row) => {
                const cards: DispatchCard[] = [...row.cards];
                // 不足分を空スロットで埋める(route-planner はレイアウト目的で視覚的に均す)
                // ただし dnd-kit の id 重複を避けるため、実カードのみ表示し余剰は背景扱い
                return (
                  <VehicleRowView
                    key={`${row.rowId}-cards`}
                    vehicle={vehicle}
                    rowId={row.rowId}
                    cards={cards}
                    enabled={vehicleEnabled}
                    selectedCardIds={selectedCardIds}
                    duplicateStoreNames={duplicateStoreNames}
                    routeDeviationCardIds={routeDeviationCardIds}
                    geoAlerts={geoAlerts}
                    displayMetrics={displayMetrics}
                    showVehicleInfo={false}
                    dropPreviewIndex={dropPreview?.rowId === row.rowId ? dropPreview.index : null}
                    recentlyMovedCardIds={recentlyMovedCardIds}
                    onSelectCard={onSelectCard}
                  />
                );
              })}
            </Box>
            {!isLastGroup && <Box sx={{ borderBottom: '1px solid hsl(var(--border) / 0.4)' }} />}
          </Box>
        );
      })}
    </Box>
  );
});

export default ConvenienceDispatchBoard;
